import { userScopeKey } from '../scope';
import { uuidV4 } from './ids';
import { encodePayload, trimToNull } from './payload';
import { appendHistoryEvent } from './repository';
import type { BookHistoryEvent, BookHistoryEventType, BookHistoryPayload, HistoryEventSource } from './types';

/**
 * Port of lib/services/history_logger.dart. Every helper is fire-and-forget:
 * failures are logged to the console and never reach the caller. The returned
 * promise only exists so tests (and callers that care) can await the write.
 */

export interface LogEventInput {
  bookId: string;
  type: BookHistoryEventType;
  payload?: BookHistoryPayload;
  sessionId?: string | null;
  source?: HistoryEventSource | string | null;
  dedupeKey?: string | null;
  timestampMs?: number;
  /** Account scope; defaults to the signed-in user. Events without a scope are dropped. */
  scope?: string | null;
}

export interface HistoryLoggerDeps {
  append: (scope: string, event: BookHistoryEvent) => Promise<unknown>;
  scope: () => string | null;
  now: () => number;
  uuid: () => string;
}

/** Seconds (player positions) → whole milliseconds, like Dart's `Duration.inMilliseconds`. */
export function secondsToMs(seconds: number): number {
  return Math.max(0, Math.floor(seconds * 1000));
}

export class HistoryLogger {
  private readonly deps: HistoryLoggerDeps;

  constructor(deps: Partial<HistoryLoggerDeps> = {}) {
    this.deps = {
      append: deps.append ?? appendHistoryEvent,
      scope: deps.scope ?? (() => userScopeKey()),
      now: deps.now ?? Date.now,
      uuid: deps.uuid ?? uuidV4,
    };
  }

  async logEvent(input: LogEventInput): Promise<void> {
    const bookId = input.bookId.trim();
    if (!bookId) return;
    const scope = (input.scope === undefined ? this.deps.scope() : input.scope)?.trim();
    if (!scope) return;
    const event: BookHistoryEvent = {
      id: this.deps.uuid(),
      bookId,
      timestampMs: input.timestampMs ?? this.deps.now(),
      type: input.type,
      payload: encodePayload(input.payload ?? {}),
      sessionId: trimToNull(input.sessionId),
      source: trimToNull(input.source),
      dedupeKey: trimToNull(input.dedupeKey),
    };
    try {
      await this.deps.append(scope, event);
    } catch (err) {
      console.warn('[history] failed to append event', err);
    }
  }

  /** `"<prefix>:<subject>:<epoch seconds>"`: suppresses double logging within the same second. */
  bucketedDedupeKey(prefix: string, subject: string): string {
    return `${prefix}:${subject}:${Math.floor(this.deps.now() / 1000)}`;
  }

  logBookmarkCreated(i: { bookId: string; bookmarkId: string; positionMs: number; sessionId?: string | null }): Promise<void> {
    return this.logEvent({
      bookId: i.bookId,
      type: 'bookmarkCreated',
      payload: { bookmarkId: i.bookmarkId, toPositionMs: i.positionMs },
      sessionId: i.sessionId,
      source: 'bookmark_provider',
      dedupeKey: `bookmark_created:${i.bookmarkId}`,
    });
  }

  logBookmarkRemoved(i: { bookId: string; bookmarkId: string }): Promise<void> {
    return this.logEvent({
      bookId: i.bookId,
      type: 'bookmarkRemoved',
      payload: { bookmarkId: i.bookmarkId },
      source: 'bookmark_provider',
      dedupeKey: `bookmark_removed:${i.bookmarkId}`,
    });
  }

  /** `scope` defaults to the signed-in account (pass the one captured before an awaited request). */
  logProgressReset(bookId: string, scope?: string | null): Promise<void> {
    return this.logEvent({ bookId, type: 'progressReset', source: 'library_provider', scope });
  }

  logMarkedFinished(bookId: string, scope?: string | null): Promise<void> {
    return this.logEvent({ bookId, type: 'markedFinished', source: 'library_provider', scope });
  }

  logMarkedUnfinished(bookId: string, scope?: string | null): Promise<void> {
    return this.logEvent({ bookId, type: 'markedUnfinished', source: 'library_provider', scope });
  }

  private logDownload(type: BookHistoryEventType, prefix: string, bookId: string, scope?: string | null): Promise<void> {
    return this.logEvent({ bookId, type, source: 'download_provider', dedupeKey: this.bucketedDedupeKey(prefix, bookId), scope });
  }

  logDownloadStarted(bookId: string, scope?: string | null): Promise<void> {
    return this.logDownload('downloadStarted', 'download_started', bookId, scope);
  }

  logDownloadCompleted(bookId: string, scope?: string | null): Promise<void> {
    return this.logDownload('downloadCompleted', 'download_completed', bookId, scope);
  }

  logDownloadRemoved(bookId: string, scope?: string | null): Promise<void> {
    return this.logDownload('downloadRemoved', 'download_removed', bookId, scope);
  }

  logDownloadCancelled(bookId: string, scope?: string | null): Promise<void> {
    return this.logDownload('downloadCancelled', 'download_cancelled', bookId, scope);
  }

  logPlaybackStarted(i: { bookId: string; sessionId?: string | null; positionMs?: number | null; resumed: boolean }): Promise<void> {
    const positionMs = i.positionMs ?? null;
    return this.logEvent({
      bookId: i.bookId,
      type: i.resumed ? 'playbackResumed' : 'playbackStarted',
      payload: { toPositionMs: positionMs ?? undefined },
      sessionId: i.sessionId,
      source: 'player_provider',
      dedupeKey: this.bucketedDedupeKey(i.resumed ? 'playback_resumed' : 'playback_started', `${i.bookId}:${positionMs ?? -1}`),
    });
  }

  logPlaybackPaused(i: { bookId: string; sessionId?: string | null; positionMs?: number | null; stopped: boolean }): Promise<void> {
    const positionMs = i.positionMs ?? null;
    return this.logEvent({
      bookId: i.bookId,
      type: i.stopped ? 'playbackStopped' : 'playbackPaused',
      payload: { toPositionMs: positionMs ?? undefined },
      sessionId: i.sessionId,
      source: 'player_provider',
      dedupeKey: this.bucketedDedupeKey(i.stopped ? 'playback_stopped' : 'playback_paused', `${i.bookId}:${positionMs ?? -1}`),
    });
  }

  logSeek(i: {
    bookId: string;
    fromMs: number;
    toMs: number;
    sessionId?: string | null;
    reason?: string | null;
    type?: Extract<BookHistoryEventType, 'seek' | 'jumpBackward' | 'jumpForward'>;
  }): Promise<void> {
    return this.logEvent({
      bookId: i.bookId,
      type: i.type ?? 'seek',
      payload: { fromPositionMs: i.fromMs, toPositionMs: i.toMs, reason: i.reason ?? undefined },
      sessionId: i.sessionId,
      source: 'player_provider',
    });
  }

  logChapterNavigation(i: {
    bookId: string;
    type: Extract<BookHistoryEventType, 'chapterSelected' | 'chapterNext' | 'chapterPrevious'>;
    chapterId: string;
    chapterTitle: string;
    chapterIndex: number;
    targetMs: number;
    sessionId?: string | null;
  }): Promise<void> {
    return this.logEvent({
      bookId: i.bookId,
      type: i.type,
      payload: { chapterId: i.chapterId, chapterTitle: i.chapterTitle, chapterIndex: i.chapterIndex, toPositionMs: i.targetMs },
      sessionId: i.sessionId,
      source: 'player_provider',
    });
  }

  logPlaybackSpeedChanged(i: { bookId: string; speed: number; sessionId?: string | null }): Promise<void> {
    return this.logEvent({ bookId: i.bookId, type: 'playbackSpeedChanged', payload: { playbackSpeed: i.speed }, sessionId: i.sessionId, source: 'player_provider' });
  }

  logSleepTimerSet(i: { bookId: string; durationMs: number; sessionId?: string | null }): Promise<void> {
    return this.logEvent({
      bookId: i.bookId,
      type: 'sleepTimerSet',
      payload: { sleepTimerDurationMs: i.durationMs },
      sessionId: i.sessionId,
      source: 'player_provider',
    });
  }

  logSleepTimerCancelled(i: { bookId: string; sessionId?: string | null }): Promise<void> {
    return this.logEvent({ bookId: i.bookId, type: 'sleepTimerCancelled', sessionId: i.sessionId, source: 'player_provider' });
  }

  logSleepTimerExpired(i: { bookId: string; sessionId?: string | null }): Promise<void> {
    return this.logEvent({ bookId: i.bookId, type: 'sleepTimerExpired', sessionId: i.sessionId, source: 'player_provider' });
  }

  logBookAction(i: { bookId: string; action: string; source?: string | null }): Promise<void> {
    return this.logEvent({ bookId: i.bookId, type: 'bookAction', payload: { reason: i.action }, source: i.source ?? 'app' });
  }
}

/** App-wide logger (signed-in user scope, IndexedDB). */
export const historyLogger = new HistoryLogger();
